import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import i18n from '@/locales/config';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configResponsive } from 'ahooks';
import { App, ConfigProvider, ConfigProviderProps, theme } from 'antd';
import pt_BR from 'antd/lib/locale/pt_BR';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import ru_RU from 'antd/locale/ru_RU';
import vi_VN from 'antd/locale/vi_VN';
import zhCN from 'antd/locale/zh_CN';
import zh_HK from 'antd/locale/zh_HK';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import React, { useEffect, useMemo, useState } from 'react';
import { RouterProvider } from 'react-router';
import { ThemeProvider, useTheme } from './components/theme-provider';
import { SidebarProvider } from './components/ui/sidebar';
import { TooltipProvider } from './components/ui/tooltip';
import { DEFAULT_LANGUAGE_CODE, ThemeEnum } from './constants/common';
import { useFetchThemeConfig } from './hooks/use-user-setting-request';
import { routers } from './routes.tsx';
import { derivePalette, hexToHslTriplet } from './theme/color-utils';
import {
  brandColors,
  brandColorsDark,
  chatSurfacesDark,
  chatSurfacesLight,
  componentTokens,
  designTokensDark,
  designTokensLight,
  markdownRhythm,
} from './theme/tokens';
import storage from './utils/authorization-util';

import 'react-photo-view/dist/react-photo-view.css';

configResponsive({
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1780,
  '4xl': 1980,
});

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const AntLanguageMap = {
  en: enUS,
  zh: zhCN,
  'zh-TRADITIONAL': zh_HK,
  ru: ru_RU,
  vi: vi_VN,
  // Keep locale key aligned with stored language value and LanguageAbbreviation.PtBr.
  'pt-br': pt_BR,
  de: deDE,
};

// if (process.env.NODE_ENV === 'development') {
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   whyDidYouRender(React, {
//     trackAllPureComponents: true,
//     trackExtraHooks: [],
//     logOnDifferentValues: true,
//   });
// }
if (process.env.NODE_ENV === 'development') {
  import('@welldone-software/why-did-you-render').then(
    (whyDidYouRenderModule) => {
      const whyDidYouRender = whyDidYouRenderModule.default;
      whyDidYouRender(React, {
        trackAllPureComponents: true,
        trackExtraHooks: [],
        logOnDifferentValues: true,
        exclude: [/^RouterProvider$/],
      });
    },
  );
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

type Locale = ConfigProviderProps['locale'];

function Root({ children }: React.PropsWithChildren) {
  const { theme: themeragflow } = useTheme();
  const { data: themeConfig } = useFetchThemeConfig();
  const getLocale = (lng: string) =>
    AntLanguageMap[lng as keyof typeof AntLanguageMap] ?? enUS;

  const [locale, setLocal] = useState<Locale>(
    getLocale(storage.getLanguage() || DEFAULT_LANGUAGE_CODE),
  );

  i18n.on('languageChanged', function (lng: string) {
    storage.setLanguage(lng);
    setLocal(getLocale(lng));
    // Should reflect to <html lang="...">
    document.documentElement.lang = lng;
  });

  const isDark = themeragflow === 'dark';
  const customPrimary = themeConfig?.primaryColor;

  const antdTokens = useMemo(() => {
    const base: Record<string, string | number> = isDark
      ? { ...designTokensDark }
      : { ...designTokensLight };
    if (customPrimary) {
      const palette = derivePalette(customPrimary, isDark);
      base.colorPrimary = palette.primary;
      base.colorLink = palette.primary;
      base.colorPrimaryHover = palette.primaryHover;
      base.colorPrimaryBg = palette.primaryBg;
    }
    return base;
  }, [isDark, customPrimary]);

  useEffect(() => {
    const root = document.documentElement;
    const brand = isDark ? brandColorsDark : brandColors;
    const effectivePrimary = customPrimary || String(antdTokens.colorPrimary);
    const triplet = hexToHslTriplet(effectivePrimary);
    const [h, s, l] = triplet.split(' ').map((v) => parseInt(v));

    root.style.setProperty('--primary', triplet);
    root.style.setProperty('--accent', triplet);
    root.style.setProperty('--ring', triplet);
    root.style.setProperty(
      '--accent-primary',
      isDark ? `hsl(${h} ${s}% ${Math.max(l, 60)}%)` : `hsl(${triplet})`,
    );

    root.style.setProperty('--sidebar-primary', triplet);
    root.style.setProperty('--sidebar-ring', triplet);
    root.style.setProperty(
      '--sidebar-accent',
      `${h} ${s}% ${isDark ? 20 : 95}%`,
    );
    root.style.setProperty(
      '--sidebar-accent-foreground',
      `${h} ${s}% ${isDark ? 80 : 40}%`,
    );

    // Clamp lightness to a readable range for the banner gradient,
    // then create subtle variation around the base value.
    const baseL = Math.max(30, Math.min(l, isDark ? 55 : 50));
    const gradientL1 = baseL;
    const gradientL2 = Math.max(25, baseL - 5);
    const gradientL3 = Math.max(20, baseL - 10);
    const gradientS2 = Math.min(s + 5, 100);
    const gradientS3 = Math.min(s + 10, 100);
    root.style.setProperty(
      '--gradient-banner',
      `linear-gradient(135deg, hsl(${h} ${s}% ${gradientL1}%) 0%, hsl(${h} ${gradientS2}% ${gradientL2}%) 50%, hsl(${h} ${gradientS3}% ${gradientL3}%) 100%)`,
    );
    root.style.setProperty(
      '--shadow-card-hover',
      `0 4px 12px 0 hsl(${h} ${s}% ${isDark ? 60 : 55}% / ${isDark ? 0.2 : 0.15})`,
    );

    if (brand.avatarGradient1) {
      root.style.setProperty(
        '--avatar-gradient-1-from',
        brand.avatarGradient1.from,
      );
      root.style.setProperty(
        '--avatar-gradient-1-to',
        brand.avatarGradient1.to,
      );
      root.style.setProperty(
        '--avatar-gradient-2-from',
        brand.avatarGradient2.from,
      );
      root.style.setProperty(
        '--avatar-gradient-2-to',
        brand.avatarGradient2.to,
      );
      root.style.setProperty(
        '--avatar-gradient-3-from',
        brand.avatarGradient3.from,
      );
      root.style.setProperty(
        '--avatar-gradient-3-to',
        brand.avatarGradient3.to,
      );
      root.style.setProperty(
        '--avatar-gradient-4-from',
        brand.avatarGradient4.from,
      );
      root.style.setProperty(
        '--avatar-gradient-4-to',
        brand.avatarGradient4.to,
      );
    }
    if (brand.avatarMarble) {
      brand.avatarMarble.forEach((color: string, i: number) => {
        root.style.setProperty(`--avatar-marble-${i + 1}`, color);
      });
    }

    // Flexar: поверхность блока кода и вертикальный ритм markdown-ответа — из токенов,
    // как и остальные поверхности; статические значения в tailwind.css служат запасом
    // до первого прогона этого эффекта.
    // Flexar: sync shared chat surfaces with the active theme for every chat host.
    Object.entries(isDark ? chatSurfacesDark : chatSurfacesLight).forEach(
      ([name, value]) => {
        root.style.setProperty(name, value);
      },
    );
    root.style.setProperty('--bg-code', brand.codeBackground);
    root.style.setProperty('--text-code', brand.codeText);
    Object.entries(markdownRhythm).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });

    if (customPrimary) {
      const palette = derivePalette(customPrimary, isDark);
      root.style.setProperty(
        '--background',
        hexToHslTriplet(palette.background),
      );
      root.style.setProperty(
        '--sidebar-background',
        hexToHslTriplet(palette.sidebarBackground),
      );
      root.style.setProperty('--card', hexToHslTriplet(palette.cardBackground));
      root.style.setProperty(
        '--popover',
        hexToHslTriplet(palette.cardBackground),
      );
    } else {
      if (brand.background) {
        root.style.setProperty(
          '--background',
          hexToHslTriplet(brand.background),
        );
      }
      if (brand.sidebarBackground) {
        root.style.setProperty(
          '--sidebar-background',
          hexToHslTriplet(brand.sidebarBackground),
        );
      }
      if (brand.cardBackground) {
        const cardTriplet = hexToHslTriplet(brand.cardBackground);
        root.style.setProperty('--card', cardTriplet);
        root.style.setProperty('--popover', cardTriplet);
      }
    }
  }, [isDark, customPrimary, antdTokens.colorPrimary]);

  useEffect(() => {
    const faviconHref = themeConfig?.favicon || '/favicon.svg';
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconHref;
  }, [themeConfig?.favicon]);

  return (
    <>
      <ConfigProvider
        theme={{
          token: antdTokens,
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          components: {
            Menu: isDark
              ? componentTokens.Menu.dark
              : componentTokens.Menu.light,
          },
        }}
        locale={locale}
      >
        <SidebarProvider className="h-full">
          <App className="w-full h-dvh relative">{children}</App>
        </SidebarProvider>
        <Sonner position={'top-right'} expand richColors closeButton></Sonner>
        <Toaster />
      </ConfigProvider>
    </>
  );
}

const RootProvider = ({ children }: React.PropsWithChildren) => {
  useEffect(() => {
    // Because the language is saved in the backend, a token is required to obtain the api. However, the login page cannot obtain the language through the getUserInfo api, so the language needs to be saved in localstorage.
    const lng = storage.getLanguage() || DEFAULT_LANGUAGE_CODE;
    i18n.changeLanguage(lng);
  }, []);

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          // Start with light mode to match Flexar default visual baseline.
          defaultTheme={ThemeEnum.Light}
          storageKey="ragflow-ui-theme"
        >
          <Root>{children}</Root>
        </ThemeProvider>
      </QueryClientProvider>
    </TooltipProvider>
  );
};

const RouterProviderWrapper: React.FC<{ router: typeof routers }> = ({
  router,
}) => {
  return <RouterProvider router={router}></RouterProvider>;
};
RouterProviderWrapper.whyDidYouRender = false;

export default function AppContainer() {
  // const [router, setRouter] = useState<any>(null);

  // useEffect(() => {
  //   getRouter().then(setRouter);
  // }, []);

  // if (!router) {
  //   return <div>Loading...</div>;
  // }

  return (
    <RootProvider>
      <RouterProviderWrapper router={routers} />
    </RootProvider>
  );
}
